import axios from 'axios';
import { API_BASE_URL } from '../api';
import type { CategoryCode, CategoryTree, LotListItem, LotRefsResponse, LotSearchParams, searchCategoryCodes } from './lots';
import type { SupplierListItem } from './suppliers';
import { readProcurementSubject } from '../utils/procurementSubject';

// Public requests never carry account tokens/cookies or run authentication retries.
const publicApi = axios.create({ baseURL: API_BASE_URL, withCredentials: false, timeout: 15000 });
export type PreviewLotFilters = Omit<LotSearchParams, 'skip' | 'limit' | 'cursor'>;
export interface ProcurementPreviewResponse<T> { items: T[]; preview: true; limit: 10; has_more: boolean }
export const previewLots = async (params: PreviewLotFilters, signal?: AbortSignal): Promise<ProcurementPreviewResponse<LotListItem>> =>
  (await publicApi.get('/procurement/preview/lots', { params, signal, paramsSerializer: { indexes: null } })).data;
export const previewSuppliers = async (params: { q?: string; region?: string }, signal?: AbortSignal): Promise<ProcurementPreviewResponse<SupplierListItem>> =>
  (await publicApi.get('/procurement/preview/suppliers', { params, signal })).data;
export const getPreviewRefs = async (signal?: AbortSignal): Promise<LotRefsResponse> =>
  (await publicApi.get('/procurement/preview/refs', { signal })).data;
export const getPreviewCategoryTree = async (signal?: AbortSignal): Promise<CategoryTree> =>
  (await publicApi.get('/procurement/preview/categories/tree', { signal })).data;
export const searchPreviewCategoryCodes = async (params: Parameters<typeof searchCategoryCodes>[0], signal?: AbortSignal): Promise<{ items: CategoryCode[]; total: number }> =>
  (await publicApi.get('/procurement/preview/categories/codes', { params, signal })).data;

/** Keep display URLs compatible with the full search; never forward pagination. */
export function previewFiltersFromParams(params: URLSearchParams): PreviewLotFilters {
  const result: Record<string, unknown> = {};
  const subject = readProcurementSubject(params.get('subject_type'));
  if (subject) result.subject_type = subject;
  const aliases: Record<string, string> = { anno: 'anno_number', customer: 'customer_name', section: 'kpved_section', cat_group: 'group' };
  for (const key of ['q', 'lot_number', 'anno', 'customer_bin', 'customer', 'section', 'cat_group', 'region', 'date_from', 'date_to', 'sort']) {
    const value = params.get(key)?.trim(); if (value) result[aliases[key] || key] = value;
  }
  for (const key of ['amount_from', 'amount_to']) {
    const raw = params.get(key); const value = Number(raw);
    if (raw && Number.isFinite(value)) result[key] = value;
  }
  for (const [key, target] of [['status', 'status'], ['method', 'trade_method']]) {
    const values = (params.get(key) || '').split(',').filter(Boolean).map(Number).filter(Number.isFinite);
    if (values.length) result[target] = values;
  }
  const codes = (params.get('enstru') || '').split(',').map(value => value.trim()).filter(Boolean);
  if (codes.length) { result.enstru_code = codes; delete result.kpved_section; delete result.group; }
  return result as PreviewLotFilters;
}
